/**
 * Suppresses uncaught runtime errors and unhandled promise rejections
 * originating from third-party browser extensions (such as MetaMask inpage.js connection failures)
 * to prevent Create React App's development error overlay from blocking the application.
 */

const EXTENSION_PATTERNS = [
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'safari-web-extension://',
  'nkbihfbeogaeaoehlefnkodbefgpgknn',
  'failed to connect to metamask',
  'metamask',
  'inpage.js',
];

function isExtensionError(event: any): boolean {
  if (!event) return false;

  try {
    const error = event.error || event.reason;
    let message = '';
    let stack = '';
    const filename = event.filename || '';

    if (typeof event.message === 'string') {
      message += ' ' + event.message;
    }

    if (error) {
      if (typeof error === 'string') {
        message += ' ' + error;
      } else {
        if (error.message) message += ' ' + error.message;
        if (error.stack) stack += ' ' + error.stack;
        if (error.name) message += ' ' + error.name;
      }
    }

    if (event.reason) {
      if (typeof event.reason === 'string') {
        message += ' ' + event.reason;
      } else if (event.reason.message) {
        message += ' ' + event.reason.message;
        if (event.reason.stack) stack += ' ' + event.reason.stack;
      }
    }

    const combined = `${message} ${stack} ${filename}`.toLowerCase();
    return EXTENSION_PATTERNS.some((pattern) => combined.includes(pattern));
  } catch {
    return false;
  }
}

if (typeof window !== 'undefined') {
  const handleIntercept = (e: any) => {
    if (isExtensionError(e)) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') {
        e.stopImmediatePropagation();
      }
      return true;
    }
  };

  // Intercept in capture phase before CRA react-error-overlay receives the event
  window.addEventListener('error', handleIntercept, true);
  window.addEventListener('unhandledrejection', handleIntercept, true);

  // Fallback for window.onerror
  const prevOnError = window.onerror;
  window.onerror = function (msg, url, line, col, err) {
    const str = `${msg || ''} ${url || ''} ${err?.stack || ''}`.toLowerCase();
    if (EXTENSION_PATTERNS.some((pattern) => str.includes(pattern))) {
      return true;
    }
    if (typeof prevOnError === 'function') {
      return prevOnError.apply(this, [msg, url, line, col, err]);
    }
    return false;
  };

  // Filter extension noise from console.error to keep development logs clean
  const origConsoleError = console.error;
  console.error = function (...args: any[]) {
    try {
      const message = args
        .map((arg) =>
          typeof arg === 'string'
            ? arg
            : arg?.message || arg?.stack || ''
        )
        .join(' ')
        .toLowerCase();

      if (EXTENSION_PATTERNS.some((p) => message.includes(p))) {
        return;
      }
    } catch {
      // ignore
    }
    origConsoleError.apply(console, args);
  };

  // MutationObserver to safely remove any error overlay iframe displaying extension errors
  if (typeof MutationObserver !== 'undefined') {
    const dismissMatchingIframes = () => {
      try {
        const iframes = document.querySelectorAll('iframe');
        iframes.forEach((iframe) => {
          try {
            const text = (
              iframe.contentDocument?.body?.innerText || ''
            ).toLowerCase();
            if (EXTENSION_PATTERNS.some((pattern) => text.includes(pattern))) {
              iframe.remove();
            }
          } catch {
            // Ignore cross-origin frame access exceptions
          }
        });
      } catch {
        // ignore
      }
    };

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (let i = 0; i < mutation.addedNodes.length; i++) {
          const node = mutation.addedNodes[i] as HTMLElement;
          if (node && node.nodeName === 'IFRAME') {
            const iframe = node as HTMLIFrameElement;
            const checkAndRemove = () => {
              try {
                const text = (
                  iframe.contentDocument?.body?.innerText || ''
                ).toLowerCase();
                if (
                  EXTENSION_PATTERNS.some((pattern) => text.includes(pattern))
                ) {
                  iframe.remove();
                }
              } catch {
                // Ignore cross-origin
              }
            };
            checkAndRemove();
            setTimeout(checkAndRemove, 100);
            setTimeout(checkAndRemove, 500);
          }
        }
      }
    });

    if (document.documentElement) {
      observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
      dismissMatchingIframes();
    }
  }
}

export {};
